/**
 * WebSocketContext для realtime сообщений (TODO-018b)
 * Дата: 15.01.2026
 * Дата: 24.01.2026 - Добавлено структурированное логирование
 *
 * СВЯЗАННЫЕ МОДУЛИ И ФАЙЛЫ:
 * - `frontend/src/hooks/useMessagesWebSocket.ts` — низкоуровневое подключение/автореконнект
 * - `frontend/src/hooks/useMessages.ts` — использует status для fallback polling
 * - `frontend/src/pages/Messages.tsx` — показывает ConnectionIndicator и NewMessageToast
 *
 * КАК РАБОТАЕТ (кратко):
 * - Провайдер поднимает WS соединение и слушает события `{type, payload}`
 * - На событиях обновляет React Query кеш (invalidate) и управляет toast о новых сообщениях
 *
 * СХЕМА:
 * - Композиция функций:
 *   - WebSocketProvider -> useMessagesWebSocket(onEvent) -> handleEvent -> react-query invalidate
 * - Схема вызовов:
 *   - new_message -> invalidate conversations + messages(phone)
 *   - message_status -> invalidate messages
 * - Использование из других модулей:
 *   - MessagesPage оборачивает страницу этим provider'ом
 */
import React, { createContext, useCallback, useContext, useMemo, useState, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { logger } from '@/utils/logger';
import {
  type NewMessagePayload,
  type MessagesWsEvent,
  type WebSocketConnectionStatus,
  useMessagesWebSocket,
} from '@/hooks/useMessagesWebSocket';

export interface NewMessageToastData {
  payload: NewMessagePayload;
  receivedAt: string; // ISO
}

interface WebSocketContextValue {
  status: WebSocketConnectionStatus;
  toast: NewMessageToastData | null;
  dismissToast: () => void;
  openToastConversation: () => void;
}

const WebSocketContext = createContext<WebSocketContextValue>({
  status: 'offline',
  toast: null,
  dismissToast: () => {},
  openToastConversation: () => {},
});

export function useWebSocketContext() {
  return useContext(WebSocketContext);
}

function playSoundSafe() {
  try {
    // Простой "бип" через WebAudio
    const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = 880;
    gain.gain.value = 0.05;
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start();
    osc.stop(ctx.currentTime + 0.08);
    osc.onended = () => {
      ctx.close().catch(() => {});
    };
  } catch {
    // ignore
  }
}

interface WebSocketProviderProps {
  tenantId?: string;
  activePhone?: string | null;
  onOpenConversation: (phone: string) => void;
  children: React.ReactNode;
}

export function WebSocketProvider({ tenantId, activePhone, onOpenConversation, children }: WebSocketProviderProps) {
  const queryClient = useQueryClient();
  const [toast, setToast] = useState<NewMessageToastData | null>(null);

  const dismissToast = useCallback(() => setToast(null), []);

  const openToastConversation = useCallback(() => {
    if (!toast) return;
    onOpenConversation(toast.payload.phone_normalized || toast.payload.phone);
    setToast(null);
  }, [onOpenConversation, toast]);

  const handleEvent = useCallback(
    (event: MessagesWsEvent) => {
      if (!tenantId) return;

      if (event.type === 'new_message') {
        const payload = event.payload as NewMessagePayload;
        
        // Дата: 24.01.2026 — структурированное логирование вместо console.debug
        logger.debug('ws_new_message', { 
          channel: payload.channel,
          direction: payload.direction,
          // НЕ логируем phone (PII)
        });

        // 1) Обновить списки/чаты (React Query)
        queryClient.invalidateQueries({ queryKey: ['conversations', tenantId] });
        
        // Дата: 17.01.2026 — исправлен predicate: проверяем phone в любой позиции
        const phoneMatch = payload.phone_normalized || payload.phone;
        queryClient.invalidateQueries({
          predicate: (q) => {
            const key = q.queryKey as any[];
            if (key?.[0] !== 'messages') return false;
            // key = ['messages', phone, channel, tenantId]
            // Проверяем совпадение phone (key[1]) с любым из вариантов
            return key?.[1] === phoneMatch || key?.[1] === payload.phone || key?.[1] === payload.phone_normalized;
          },
        });

        // Дата: 23.02.2026 — FIX: при входящем WABA-сообщении обновляем статус 24h окна
        // Без этого окно остаётся «закрыто» до следующего refetch (60с)
        if (payload.direction === 'incoming' && (payload.channel === 'whatsapp_waba' || payload.channel === 'waba')) {
          queryClient.invalidateQueries({ queryKey: ['waba-window-status', tenantId, phoneMatch] });
        }

        // 2) Toast если сообщение не в активной беседе
        const normalized = payload.phone_normalized || payload.phone;
        if (normalized && activePhone && normalized === activePhone) {
          return;
        }

        setToast({ payload, receivedAt: new Date().toISOString() });
        playSoundSafe();
        return;
      }

      if (event.type === 'message_status') {
        // Статусы влияют на отображение в открытом чате → обновляем messages
        queryClient.invalidateQueries({ queryKey: ['messages'] });
        return;
      }
    },
    [activePhone, queryClient, tenantId]
  );

  const { status } = useMessagesWebSocket({ tenantId, enabled: !!tenantId, onEvent: handleEvent });
  
  // Дата: 24.01.2026 - Логируем изменения статуса WS
  const prevStatusRef = useRef(status);
  useEffect(() => {
    if (prevStatusRef.current !== status) {
      logger.info('ws_status_change', {
        from: prevStatusRef.current,
        to: status,
      });
      prevStatusRef.current = status;
    }
  }, [status]);

  const value = useMemo<WebSocketContextValue>(
    () => ({
      status,
      toast,
      dismissToast,
      openToastConversation,
    }),
    [dismissToast, openToastConversation, status, toast]
  );

  return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
}

